import React from 'react'
import { getParentOrigin } from '@/lib/app-messaging'
import { useParentNavigation } from '@/hooks/use-parent-navigation'
import { cn } from '@/lib/utils'

interface ParentNewTabLinkProps {
  /** Route in the parent (shell) application, e.g. /next/contact/userprofile/123 */
  route: string
  children: React.ReactNode
  className?: string
}

/**
 * A link to a screen in the parent (shell) application that opens in a new tab.
 *
 * An ordinary click is handed to the shell as a navigation message, which is what every other
 * cross-app link in this app does. The anchor still carries a real href so the browser keeps its
 * own affordances: middle-click, ctrl/cmd-click, and "Open link in new tab" from the native
 * context menu. No context menu of our own.
 *
 * The href needs the shell's origin because this app is served from its own; where that cannot be
 * derived, the link falls back to a button (messaged navigation only), and outside an iframe it is
 * plain text.
 */
export function ParentNewTabLink({ route, children, className }: ParentNewTabLinkProps) {
  const { navigate, shouldRenderNavigationLinks } = useParentNavigation()
  const parentOrigin = getParentOrigin()

  if (!shouldRenderNavigationLinks) {
    return <span className={className}>{children}</span>
  }

  const openInParent = () => navigate(route, { openInNewTab: true })

  if (!parentOrigin) {
    return (
      <button type="button" onClick={openInParent} className={cn('text-left', className)}>
        {children}
      </button>
    )
  }

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // Leave anything but a plain left click to the browser, so the href does its job.
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return
    }
    event.preventDefault()
    openInParent()
  }

  return (
    <a
      href={`${parentOrigin}${route}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      className={cn('text-left', className)}
    >
      {children}
    </a>
  )
}

export default ParentNewTabLink
